import { getProgressTrackerItemState, ProgressTracker } from "@octopusdeploy/design-system-components";
import { links, type LinkHref } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { useLayoutEffect } from "react";
import { Action, useAnalyticActionDispatch } from "~/analytics/Analytics";
import { ProjectStatusItem } from "~/areas/projects/components/ProjectStatus/ProjectStatusItem";
import useLocalStorage from "~/hooks/useLocalStorage";
import type { ProjectTemplateStatusAction } from "./projectTemplateStatusActions";
import type { ProjectTemplateStatusState } from "./projectTemplateStatusReducer";
import { projectTemplateStatusItems } from "./projectTemplateStatusReducer";
import { useProjectTemplateStatus } from "./useProjectTemplateStatus";
interface ProjectTemplateStatusProps {
    slug?: string | undefined;
    gitRef: string;
    hideCloseButton?: boolean;
    state?: ProjectTemplateStatusState;
    dispatch: React.Dispatch<ProjectTemplateStatusAction>;
    enableLinks?: boolean;
    onRefetchReady?: (refetch: () => void) => void;
}
export const ProjectTemplateStatusBar = ({ state, dispatch, slug, gitRef, hideCloseButton, enableLinks = false, onRefetchReady }: ProjectTemplateStatusProps): JSX.Element | null => {
    const dispatchAction = useAnalyticActionDispatch();
    const [dismissed, setDismissed] = useLocalStorage(`Octopus.ProjectTemplate.${slug}.Status.Dismissed`, false);
    const { refetch } = useProjectTemplateStatus(dispatch, slug, dismissed, gitRef);
    useLayoutEffect(() => {
        if (onRefetchReady && refetch) {
            onRefetchReady(refetch);
        }
    }, [onRefetchReady, refetch]);
    const closeProjectTemplateStatus = () => {
        setDismissed(true);
        dispatchAction("Project Template Status Bar Dismissed", { action: Action.Toggle, resource: "Project Template Status Bar" });
    };
    if (!state?.hasLoadedServerState)
        return null;
    const allStepsComplete = state?.serverState?.HasSteps && state?.serverState?.HasPublishedVersions && state?.serverState?.HasSharedToSpaces;
    if (dismissed || !state || allStepsComplete)
        return null;
    return (<ProgressTracker onClose={hideCloseButton ? undefined : closeProjectTemplateStatus} showCloseButton={!hideCloseButton} closeButtonTooltip="Once dismissed, project template status can't be re-opened">
            {state?.items.map((item, index) => {
            return (<ProjectStatusItem name={item.label} state={getProgressTrackerItemState(index, state.indexOfActiveItem)} href={enableLinks ? getLink(item.label, slug, gitRef) : undefined} key={item.label} showIndicator={false} eventName="Click On Project Template Status Bar" eventAction={item.eventAction}/>);
        })}
        </ProgressTracker>);
};
function getLink(itemLabel: string, slug: string | undefined, gitRef: string): LinkHref | undefined {
    if (!slug)
        return undefined;
    switch (itemLabel) {
        case projectTemplateStatusItems.process.label:
            return links.projectTemplateDeploymentProcessPage.generateUrl({ slug, gitRef });
        default:
            return undefined;
    }
}
